/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import ContactCard from "../../../components/ContactCard";
import { ListAndFiltersPageBodyContainer } from "../../../components/layout/PageContainer";
import { Loader, LoadingBox } from "../../../components/Loader";
import Pagination, { PaginationDetails } from "../../../components/Pagination";
import { PAGINATION_DATA_LIMIT, SORTING_OPTIONS } from "../../../util/consts";
import { RelationshipOwner, selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { ShopProductCardSwitch } from "../components/ProductCard";
import ProductList from "../components/ProductList";
import useStockAvailabilities from "../components/StockAvailability/useStockAvailabilities";
import { Product } from "../storage";
import { getFilterCategories } from "../utils/getFilterCategories";

import NoPastProducts from "./NoPastProducts";
import ProductListContainer from "./ProductsListContainer";

interface PastProductProps {
  setCategoriesCallback: (categories: string[]) => void;
  paginationCounter: React.ReactNode;
  data: Product[];
  filteredProducts: Product[];
  handleClearFilters: () => void;
  isFilterApplied: boolean;
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  paginatedData: Product[];
  noPagination: boolean;
  currentPage: number;
  relationshipOwner: RelationshipOwner;
  isPastProductsPending: boolean;
}

const PastProductsTab: React.FC<PastProductProps> = ({
  setCategoriesCallback,
  paginationCounter,
  data,
  filteredProducts,
  handleClearFilters,
  isFilterApplied,
  sortBy,
  paginatedData,
  noPagination,
  currentPage,
  relationshipOwner,
  setSortBy,
  isPastProductsPending,
}) => {
  const selectedAccount = useSelector(selectCurrentFarmerAccount);

  // paginatedData already carries branch-specific active prices (applied on the
  // Products page before filtering/sorting), so here we only merge in stock.
  const paginatedDataWithStock = useStockAvailabilities({
    products: paginatedData,
  });

  useEffect(() => {
    setCategoriesCallback(getFilterCategories(data));
  }, [data, setCategoriesCallback]);

  const isLoadingPastProducts = isPastProductsPending && data.length === 0;

  return (
    <ListAndFiltersPageBodyContainer>
      {isLoadingPastProducts && (
        <LoadingBox>
          <Loader title="Loading your products" type="inline-flex" sx={{ marginTop: "12px" }} />
        </LoadingBox>
      )}
      {data.length > 0 && !isLoadingPastProducts && (
        <ProductListContainer shrink={paginatedData.length <= 0}>
          <PaginationDetails
            onClear={handleClearFilters}
            showClear={isFilterApplied}
            paginationCounter={paginationCounter}
            sorting={{
              value: sortBy,
              onChange: setSortBy,
              options: SORTING_OPTIONS,
            }}
          />
          <ProductList
            items={paginatedDataWithStock}
            renderListItem={(item) => {
              return (
                <ShopProductCardSwitch
                  key={item.productId}
                  item={item}
                  enableQuote={!!selectedAccount?.relationshipOwner?.fullName}
                  isPastProduct
                />
              );
            }}
          />
          {!noPagination && (
            <Pagination
              page={currentPage}
              path="/products"
              count={Math.ceil(filteredProducts.length / PAGINATION_DATA_LIMIT)}
            />
          )}
          {(paginatedData.length < 25 || paginatedDataWithStock.length < 25) && (
            <ContactCard
              title={`Can't find what you're looking for?\nContact your Farmlands representative.`}
              sx={{ minWidth: "100%" }}
              {...relationshipOwner}
            />
          )}
        </ProductListContainer>
      )}

      {data.length === 0 && !isPastProductsPending && <NoPastProducts />}
    </ListAndFiltersPageBodyContainer>
  );
};

export default PastProductsTab;
